// ============================================
// Lovense Triggers — v0.2.4+ (Phase 3a + base pulse)
//
// Two responsibilities:
//   1. Sustained BASE PULSE that tracks absolute arousal level. Runs
//      continuously between story beats so the toy doesn't feel like
//      it flips on and off. Gated by the `ambient` category toggle.
//   2. Per-event SPIKES on positive arousal deltas. Overlays the base
//      temporarily, then the base resumes via lovenseManager's internal
//      _spikeResumeTimer. Gated by the `autoArousal` category toggle.
//
// Everything else (climax pattern on #stats:orgasm, master-command pulses,
// chastity lock-in) is deferred to Phase 3b once this pipeline has been
// tested end-to-end with a real toy during actual gameplay.
//
// Contract:
//  - Only fires when the Lovense integration is enabled AND paired
//  - Base and spike are independent toggles (user can have one without the other)
//  - Spike intensity scales with delta + absolute arousal
//  - Spike is rate-limited to prevent flooding on shuffle-bag events that emit
//    multiple STAT_CHANGED in quick succession (e.g. Master inspection +3)
// ============================================

import { events, Events } from '../engine/events.js';
import { lovenseManager } from './lovense.js';
import { variableManager } from '../engine/variables.js';

const MIN_DELTA = 3;                 // ignore tiny changes (e.g. +1 passive gain) for SPIKE
const MIN_INTERVAL_MS = 2500;        // at most one spike every 2.5s
const MAX_INTENSITY = 20;            // Lovense Function scale ceiling
const BASE_INTENSITY = 4;            // spike floor so even tiny gains feel like something

// Base-pulse intensity mapping: which arousal level gets which sustained level.
// Stepped (not linear) to avoid hammering the toy with tiny refresh commands on
// every passive +1 arousal gain. When the arousal crosses a bin boundary, the
// manager's setBase() no-ops if the level didn't actually change.
//
// Bin values calibrated against Edge 2 felt output: below ~4 on the Lovense
// Vibrate scale, the motor's physical response is barely perceptible. Earlier
// bins of 2/3/5/7/9 made the low-arousal levels feel like "nothing" even at
// 100% master volume, and the slider felt ineffective because half the range
// landed in the sub-4 dead zone. Raised to 4/6/9/12/15 so each bin is clearly
// above the perceptibility floor, and the master-volume curve has meaningful
// headroom to scale across.
function computeBaseIntensity(arousal) {
    if (arousal < 20)  return 0;      // pre-sensual, toy is silent
    if (arousal < 40)  return 4;      // subtle but clearly present
    if (arousal < 60)  return 6;      // noticeable
    if (arousal < 80)  return 9;      // insistent
    if (arousal < 100) return 12;     // strong
    return 15;                         // edging threshold — below spike ceiling (20)
}

class LovenseTriggers {
    constructor() {
        this._lastArousal = null;    // last known Arousal value (null until first STAT_CHANGED)
        this._lastSpikeAt = 0;       // rate limit timestamp
        this._bound = false;
    }

    /**
     * Subscribe to the event bus. Called once during app init after the
     * lovense manager singleton is ready. Safe to call multiple times —
     * subsequent calls are no-ops.
     */
    bind() {
        if (this._bound) return;
        this._bound = true;
        events.on(Events.STAT_CHANGED, (name, newVal) => this._onStat(name, newVal));

        // ── Post-beat Arousal re-read (the gameplay-driven path) ──
        // Ink-driven Arousal changes (`~ Arousal += N`, do_action arousal_extra)
        // happen INSIDE the `blockObservers = true` window of
        // _processStoryContent, so the inkjs observer bails before it can emit
        // STAT_CHANGED (variables.js _onInkVariableChanged early-returns on
        // blockObservers). syncFromInk() at beat-end mirrors the value into
        // baseVar silently, with no event. Net effect: STAT_CHANGED(Arousal)
        // is NEVER emitted for normal gameplay — only for direct JS setValue
        // writes (cheat console, a handful of minigame writes). So the toy
        // only ever buzzed when the player cheated their arousal.
        //
        // The fix: re-read Arousal at the same sync point stats.js uses
        // (_forceSyncAllFromInk on these two events). syncFromInk() runs in
        // _createChoicesUI BEFORE STORY_CHOICES_READY fires (save-state-drift
        // rule 3), so variableManager.getValue('Arousal') is already current.
        // setBase() is idempotent and spike() is rate-limited, so the retained
        // STAT_CHANGED listener above can't double-fire the toy.
        //
        // Regression origin: 0.2.5.7 (4657bf54) sealed the blockObservers fence
        // into a depth counter. Pre-seal, inner setters leaked the fence open
        // mid-beat, so late Arousal writes still reached the observer — which is
        // how the Phase-3 "tested with a real toy" run worked in 0.2.4. The
        // counter closed that leak and silently killed the gameplay trigger.
        events.on(Events.STORY_CHOICES_READY, () => this._onBeatEnd());
        events.on(Events.STORY_CONTENT_FINISHED, () => this._onBeatEnd());

        events.on(Events.GAME_NEW, () => this.reset());
        // On save load: reset the trigger's internal baseline, then after a
        // short delay re-initialize the base pulse from the loaded arousal
        // value. The delay lets game-loader.js finish restoring variables
        // before we read them. Without this step the toy stays silent after
        // Continue from main menu until the player's first action shifts
        // Arousal — which can be many clicks away in a hub location.
        events.on(Events.LOAD_COMPLETED, () => {
            this.reset();
            setTimeout(() => this._initializeFromCurrentState(), 300);
        });

        // Init-time safety net: if a session was restored from localStorage,
        // send a Stop RIGHT NOW — before the player even clicks Continue.
        // Previous tab may have left the toy running (Vibrate:N with a 30s
        // timeSec, or a 5s Preset mid-render). Without this the toy keeps
        // buzzing at the main menu until the user loads a save, which
        // could be seconds of "max intensity at arousal 0". User-reported
        // 2026-04-09. forceStop no-ops if no session exists.
        try { lovenseManager.forceStop(); } catch(e) {}
    }

    /**
     * Read the current Arousal from variableManager and seed the base
     * pulse + _lastArousal baseline without firing a spike. Used after
     * save load + after pairing (so the base catches up to current state
     * instead of waiting for the next stat event).
     */
    _initializeFromCurrentState() {
        if (!lovenseManager.isEnabled() || !lovenseManager.isPaired()) return;
        try {
            const arousal = variableManager.getValue('Arousal');
            if (typeof arousal !== 'number') return;
            this._lastArousal = arousal;
            if (lovenseManager.getCategoryToggle('ambient')) {
                lovenseManager.setBase(computeBaseIntensity(arousal));
            }
        } catch(e) { /* variableManager not ready — silent no-op */ }
    }

    /**
     * Beat-end handler — re-read the current Arousal and drive the toy.
     * Bound to STORY_CHOICES_READY + STORY_CONTENT_FINISHED so Ink-driven
     * arousal changes (which never emit STAT_CHANGED — see bind()) still
     * reach the toy. Reads the same value stats.js renders at this point.
     */
    _onBeatEnd() {
        let arousal;
        try { arousal = variableManager.getValue('Arousal'); }
        catch(e) { return; }   // variableManager not ready — silent no-op
        if (typeof arousal !== 'number') return;
        this._processArousal(arousal);
    }

    _onStat(name, newVal) {
        if (name !== 'Arousal') return;
        this._processArousal(newVal);
    }

    /**
     * Shared base-pulse + spike logic. Called from BOTH the STAT_CHANGED
     * listener (direct JS setValue path) and the beat-end re-read (Ink
     * gameplay path). Idempotent base + rate-limited spike make the two
     * paths safe to overlap on the rare beat where both fire.
     */
    _processArousal(newVal) {
        // Feature-level gate applies to BOTH base and spike
        if (!lovenseManager.isEnabled() || !lovenseManager.isPaired()) {
            this._lastArousal = newVal;  // still track for next time
            return;
        }

        // ── 1. Base pulse — continuous, tracks absolute arousal level ──
        // Fires on every Arousal change (even small passive +1 ticks) so the
        // base level stays in sync. setBase() is idempotent — no toy command
        // is sent if the computed level hasn't changed since last call.
        if (lovenseManager.getCategoryToggle('ambient')) {
            const targetBase = computeBaseIntensity(newVal);
            lovenseManager.setBase(targetBase);
        }

        // ── 2. Spike on positive delta ──
        // First event just initializes the baseline without spiking
        if (this._lastArousal === null) {
            this._lastArousal = newVal;
            return;
        }
        const prev = this._lastArousal;
        this._lastArousal = newVal;

        const delta = newVal - prev;
        if (delta < MIN_DELTA) return;

        if (!lovenseManager.getCategoryToggle('autoArousal')) return;

        // Rate limit
        const now = Date.now();
        if (now - this._lastSpikeAt < MIN_INTERVAL_MS) return;
        this._lastSpikeAt = now;

        // Map delta + absolute arousal to an intensity.
        // Delta drives the spike height (bigger gain = stronger), absolute
        // arousal lifts the baseline (high arousal feels more sensitive).
        //   +3 Arousal at 20  -> BASE + 3 + 2   = 9
        //   +5 Arousal at 50  -> BASE + 5 + 5   = 14
        //   +8 Arousal at 80  -> BASE + 8 + 8   = 20 (capped)
        const lift = Math.floor(newVal / 10);
        let intensity = BASE_INTENSITY + delta + lift;
        if (intensity > MAX_INTENSITY) intensity = MAX_INTENSITY;

        // Duration also scales with delta but capped — even +15 shouldn't
        // run longer than 6s or the next story beat will be mid-pulse.
        let duration = Math.min(6, Math.max(2, Math.floor(delta / 2) + 2));

        // Use spike() instead of pulse() so the base loop pauses cleanly and
        // resumes at the new (likely higher) arousal level after the spike.
        lovenseManager.spike(intensity, duration);
    }

    /**
     * Reset the baseline — call on new game / save load so the first
     * STAT_CHANGED after the load doesn't fire a huge spike from the
     * delta between the save's arousal and the fresh-game default.
     * Also drops the base to 0 — the next STAT_CHANGED will re-establish
     * it from the loaded save's arousal value.
     *
     * Uses forceStop (not stopBase) so we kill any lingering spike/preset
     * from the previous session regardless of the enabled toggle. Cheap
     * no-op when no session exists.
     */
    reset() {
        this._lastArousal = null;
        this._lastSpikeAt = 0;
        try { lovenseManager.forceStop(); } catch(e) {}
    }
}

export const lovenseTriggers = new LovenseTriggers();

if (typeof window !== 'undefined') {
    window._lovenseTriggers = lovenseTriggers;
}
